import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Crosshair,
  Download,
  FileCode,
  Image as ImageIcon,
  Layers,
  Minus,
  Palette,
  Plus,
  Scan,
  TriangleAlert,
  UserRound,
} from 'lucide-react';
import { Button, IconButton, Menu, Pill, SearchInput, SegmentedControl } from '../../../components/ui';
import { AuthImage } from '../../../components/media/AuthImage';
import { FullscreenButton } from './FullscreenButton';
import { KIND_ICON, KIND_LABEL, govDate, plural } from '../format';
import {
  BRANCH_PALETTE,
  CARD_H,
  CARD_W,
  KIND_COLOUR,
  ROOT_ID,
  STATUS_COLOUR,
  branchColours,
  branchWedges,
  buildChart,
  chartInsights,
  collapseBeyond,
  colourOf,
  fitText,
  frame,
  holdersByOrganization,
  labelRoom,
  lineageOf,
  peopleColour,
  pendingByOrganization,
  polar,
  radialLink,
  searchHits,
  tint,
  treeLink,
  wedgePath,
  wrapText,
  zoomAt,
  type Chart,
  type ChartItem,
  type ChartMode,
  type ColourBy,
  type Transform,
} from '../chart';
import type { GovNodeView, GovOfficeHolder, GovUpdateView } from '../types';

const MODES: Array<{ value: ChartMode; label: string }> = [
  { value: 'tree', label: 'Chart' },
  { value: 'radial', label: 'Ring' },
];

const COLOURS: Array<{ value: ColourBy; label: string; hint: string }> = [
  { value: 'branch', label: 'Branch of the state', hint: 'One colour per top-level body' },
  { value: 'kind', label: 'Kind of body', hint: 'Ministries, agencies, courts' },
  { value: 'status', label: 'Where it stands', hint: 'In the directory, confirmed, or waiting' },
  { value: 'people', label: 'People recorded', hint: 'How thickly each body is covered' },
];

/** How much of the chart the details panel covers on a wide screen */
const PANEL_W = 400;

const DEPTHS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Top' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: 'all', label: 'All' },
];

/** True while the browser is asked to keep movement to a minimum */
const useReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listen = () => setReduced(query.matches);
    listen();
    query.addEventListener?.('change', listen);
    return () => query.removeEventListener?.('change', listen);
  }, []);
  return reduced;
};

/** The size of the box the chart is drawn in */
const useBoxSize = (ref: React.RefObject<HTMLElement | null>) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const read = () =>
      setSize({ width: element.clientWidth, height: element.clientHeight });
    read();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return size;
};

/* ------------------------------------------------------------------ */
/* One entity, drawn                                                   */
/* ------------------------------------------------------------------ */

const Person: React.FC<{ item: ChartItem; colour: string }> = ({ item, colour }) => {
  const person = item.person;
  if (!person) {
    return (
      <text x={48} y={54} fontSize={11} fill="#d4d4d4">
        Nobody recorded
      </text>
    );
  }
  // Everyone else the directory records here, the one on the card aside
  const extra = person.recorded ? item.holders.length - 1 : item.holders.length;
  const width = CARD_W - 48 - (extra ? 34 : 14);
  const role = person.role ? fitText(person.role, 78, 10.5) : '';
  const name = fitText(person.name, width - (role ? 82 : 0), 11.5);
  return (
    <g>
      <circle
        cx={40}
        cy={50}
        r={7}
        fill={person.recorded ? tint(colour, 0.16) : 'none'}
        stroke={person.recorded ? 'none' : '#d4d4d4'}
        strokeWidth={1}
        strokeDasharray={person.recorded ? undefined : '2 2'}
      />
      <text
        x={40}
        y={53.5}
        fontSize={7}
        textAnchor="middle"
        fill={person.recorded ? colour : '#a3a3a3'}
        fontWeight={600}
      >
        {initials(person.name)}
      </text>
      <text x={53} y={54} fontSize={11.5} fill={person.recorded ? '#404040' : '#a3a3a3'}>
        {name}
        {role ? <tspan fill="#a3a3a3"> · {role}</tspan> : null}
      </text>
      {extra > 0 && (
        <text x={CARD_W - 14} y={54} fontSize={10.5} textAnchor="end" fill="#a3a3a3">
          +{extra}
        </text>
      )}
    </g>
  );
};

/** The marks in a card's corner: what about this body wants a person's eye */
function marks(
  item: ChartItem,
): Array<{ colour: string; title: string; hollow?: boolean }> {
  const list: Array<{ colour: string; title: string; hollow?: boolean }> = [];
  if (item.pending > 0) {
    list.push({ colour: '#ea2a33', title: `${plural(item.pending, 'change')} waiting` });
  }
  if (item.contested) {
    list.push({
      colour: '#f59e0b',
      title: `Two people are recorded as ${item.contested}`,
    });
  }
  if (item.mapPlacement) {
    list.push({
      colour: '#8b5cf6',
      title: `Filed here in the directory; the map puts it ${item.mapPlacement}`,
    });
  }
  if (item.node?.stale) {
    list.push({ colour: '#f59e0b', title: 'Not found by the latest research', hollow: true });
  }
  if (item.node && !item.inDirectory) {
    list.push({ colour: '#a3a3a3', title: 'Not in the directory yet', hollow: true });
  }
  return list;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.length === 1 ? [parts[0][0]] : [parts[0][0], parts[parts.length - 1][0]];
  return letters.join('').toUpperCase();
}

const EntityCard: React.FC<{
  item: ChartItem;
  colour: string;
  selected: boolean;
  dimmed: boolean;
  onSelect: () => void;
  onToggle: () => void;
}> = ({ item, colour, selected, dimmed, onSelect, onToggle }) => {
  const Icon = item.kind === 'country' ? null : KIND_ICON[item.kind];
  const lines = wrapText(item.label, CARD_W - 64, 12.5, 2);
  const hasChildren = item.children.length > 0 || item.hidden > 0;

  return (
    <g
      transform={`translate(${item.x},${item.y})`}
      opacity={dimmed ? 0.22 : 1}
      style={{ cursor: 'pointer' }}
      tabIndex={0}
      role="button"
      aria-label={`${item.label}${item.person ? `, ${item.person.name}` : ''}`}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      {selected && (
        <rect
          x={-4}
          y={-4}
          width={CARD_W + 8}
          height={CARD_H + 8}
          rx={16}
          fill="none"
          stroke={colour}
          strokeWidth={2}
        />
      )}
      <rect
        width={CARD_W}
        height={CARD_H}
        rx={13}
        fill="#ffffff"
        stroke={selected ? colour : '#e5e5e5'}
        strokeWidth={selected ? 1.5 : 1}
      />
      <rect
        x={12}
        y={CARD_H / 2 - 14}
        width={28}
        height={28}
        rx={9}
        fill={tint(colour, 0.12)}
      />
      {Icon && (
        <Icon
          x={19}
          y={CARD_H / 2 - 7}
          width={14}
          height={14}
          color={colour}
          strokeWidth={1.9}
        />
      )}
      {lines.map((line, index) => (
        <text
          key={index}
          x={48}
          y={(lines.length === 1 ? 26 : 20) + index * 14}
          fontSize={12.5}
          fill="#0a0a0a"
        >
          {line}
        </text>
      ))}
      <Person item={item} colour={colour} />

      {/* what needs a look, top right */}
      <g transform={`translate(${CARD_W - 12},13)`}>
        {marks(item).map((mark, index) => (
          <g key={mark.title} transform={`translate(${index * -14},0)`}>
            <circle
              r={4.5}
              fill={mark.hollow ? 'none' : mark.colour}
              stroke={mark.hollow ? mark.colour : 'none'}
              strokeDasharray={mark.hollow ? '2 2' : undefined}
            />
            <title>{mark.title}</title>
          </g>
        ))}
      </g>

      {hasChildren && (
        <g
          transform={`translate(${CARD_W},${CARD_H / 2})`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          style={{ cursor: 'pointer' }}
        >
          <circle r={11} fill="#ffffff" stroke="#e5e5e5" />
          <text
            y={item.collapsed ? 3.5 : 3}
            fontSize={item.collapsed ? 10 : 13}
            textAnchor="middle"
            fill="#737373"
          >
            {item.collapsed ? item.hidden : '–'}
          </text>
          <title>
            {item.collapsed
              ? `Show the ${plural(item.hidden, 'body', 'bodies')} under this`
              : 'Fold this branch away'}
          </title>
        </g>
      )}
    </g>
  );
};

const RadialNode: React.FC<{
  item: ChartItem;
  colour: string;
  selected: boolean;
  dimmed: boolean;
  showLabel: boolean;
  onSelect: () => void;
}> = ({ item, colour, selected, dimmed, showLabel, onSelect }) => {
  const [x, y] = polar(item.radius, item.angle);
  const size = item.depth <= 1 ? 7 : item.depth === 2 ? 5 : 3.6;
  const grown = size + Math.min(4, Math.sqrt(Math.max(0, item.people)) * 1.1);
  const left = Math.cos(item.angle) < 0;
  const degrees = (item.angle * 180) / Math.PI;
  const label =
    item.depth <= 2
      ? fitText(item.label, 168, 11)
      : fitText(item.short, 120, 10);

  return (
    <g
      transform={`translate(${x},${y})`}
      opacity={dimmed ? 0.2 : 1}
      style={{ cursor: 'pointer' }}
      tabIndex={0}
      role="button"
      aria-label={item.label}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      {selected && <circle r={grown + 5} fill="none" stroke={colour} strokeWidth={1.5} />}
      <circle
        r={grown}
        fill={item.inDirectory ? colour : '#ffffff'}
        stroke={colour}
        strokeWidth={item.inDirectory ? 1.2 : 1.4}
        strokeDasharray={item.inDirectory ? undefined : '2.5 2'}
      />
      {item.pending > 0 && (
        <circle cx={grown * 0.75} cy={-grown * 0.75} r={3} fill="#ea2a33" stroke="#fff" strokeWidth={1} />
      )}
      {showLabel && (
        <text
          transform={`rotate(${left ? degrees + 180 : degrees}) translate(${left ? -(grown + 7) : grown + 7},0)`}
          textAnchor={left ? 'end' : 'start'}
          dominantBaseline="middle"
          fontSize={item.depth <= 1 ? 11.5 : item.depth === 2 ? 10.5 : 9.5}
          fill={item.depth <= 2 ? '#404040' : '#737373'}
        >
          {label}
          {item.person && item.depth <= 2 ? (
            <tspan fill="#a3a3a3"> · {fitText(item.person.name, 110, 10)}</tspan>
          ) : null}
        </text>
      )}
      <title>
        {item.label}
        {item.person ? ` — ${item.person.name}` : ''}
      </title>
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* The chart                                                           */
/* ------------------------------------------------------------------ */

export const GovernmentChart: React.FC<{
  nodes: GovNodeView[];
  countryName: string;
  holders: GovOfficeHolder[];
  updates: GovUpdateView[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loadingPeople?: boolean;
  className?: string;
  /** Shown over the whole screen: the chart takes all the height it is given */
  expanded?: boolean;
  onToggleExpanded?: () => void;
}> = ({
  nodes,
  countryName,
  holders,
  updates,
  selectedId,
  onSelect,
  loadingPeople,
  className = '',
  expanded = false,
  onToggleExpanded,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const size = useBoxSize(boxRef);
  const reducedMotion = useReducedMotion();

  // It opens as the whole state at once — the ring, every level of it — and
  // the chart is where you go to read one branch closely
  const [mode, setMode] = useState<ChartMode>('radial');
  const [colourBy, setColourBy] = useState<ColourBy>('branch');
  const [depth, setDepth] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [hovered, setHovered] = useState<string | null>(null);
  const [transform, setTransform] = useState<Transform>({ k: 1, x: 0, y: 0 });

  const holdersByOrg = useMemo(() => holdersByOrganization(holders), [holders]);
  const pendingByOrg = useMemo(() => pendingByOrganization(updates), [updates]);

  // The whole state, folded or not: what the chart says about the country has
  // to hold however much of it is on the screen, and searching has to find
  // what is folded away
  const whole = useMemo(
    () => buildChart(nodes, { countryName, holdersByOrg, pendingByOrg }),
    [nodes, countryName, holdersByOrg, pendingByOrg],
  );
  const found = useMemo(() => searchHits(whole, search), [whole, search]);

  /** Searching opens whatever it found, without forgetting what was folded */
  const folded = useMemo(() => {
    if (!found.size) return collapsed;
    const open = new Set<string>();
    for (const id of Array.from(found)) {
      let current = whole.byId.get(id)?.parentId ?? null;
      for (let depth = 0; current && depth < 25; depth++) {
        open.add(current);
        current = whole.byId.get(current)?.parentId ?? null;
      }
    }
    return new Set(Array.from(collapsed).filter((id) => !open.has(id)));
  }, [collapsed, found, whole]);

  const chart = useMemo(
    () =>
      buildChart(nodes, {
        countryName,
        collapsed: folded,
        holdersByOrg,
        pendingByOrg,
      }),
    [nodes, countryName, folded, holdersByOrg, pendingByOrg],
  );

  const branches = useMemo(() => branchColours(whole), [whole]);
  const insights = useMemo(() => chartInsights(whole), [whole]);
  const hits = useMemo(
    () => new Set(Array.from(found).filter((id) => chart.byId.has(id))),
    [found, chart],
  );

  const hoveredItem = hovered ? (chart.byId.get(hovered) ?? null) : null;
  const selectedItem = selectedId ? (chart.byId.get(selectedId) ?? null) : null;
  const lineage = useMemo(
    () => lineageOf(hoveredItem ?? selectedItem, chart),
    [hoveredItem, selectedItem, chart],
  );

  /** What the whole chart occupies, in its own coordinates */
  const extent = useMemo(() => {
    if (mode === 'tree') return chart.bounds;
    const reach = chart.radius + 150;
    return { minX: -reach, minY: -reach, maxX: reach, maxY: reach };
  }, [mode, chart]);

  const fit = useCallback(() => {
    if (!size.width || !size.height) return;
    setTransform(
      mode === 'tree'
        ? frame(extent, size, 44, 1, 0.34)
        : frame(extent, size, 44, 1.1, 0.14),
    );
  }, [extent, size, mode]);

  /**
   * Where the chart opens.
   *
   * A state does not fit on a screen at a size anyone can read, so the chart
   * starts where the state does — the top of it, on the left, at a size meant
   * for reading — and the reader moves out from there. The ring is a shape
   * rather than a list of names, so that one opens whole.
   */
  const opening = useCallback(() => {
    if (!size.width || !size.height) return;
    if (mode === 'radial') {
      fit();
      return;
    }
    setTransform(frame(extent, size, 40, 0.92, 0.6));
  }, [mode, size, extent, fit]);

  // Frame the chart when it first has a size, when the shape changes and when
  // the reader switches between the two ways of reading it
  const framed = useRef('');
  useEffect(() => {
    if (!size.width || !size.height) return;
    // Folding a branch away must not move the chart under the reader, so the
    // view is only reset when the state itself, the shape or the box changes
    const shape = `${mode}:${nodes.length}:${Math.round(size.width)}x${Math.round(size.height)}`;
    if (framed.current === shape) return;
    framed.current = shape;
    opening();
  }, [mode, nodes.length, size, extent, opening]);

  /* ---- panning and zooming ---- */
  const drag = useRef<{ x: number; y: number; tx: number; ty: number; moved: boolean } | null>(null);
  const pinch = useRef<{ distance: number; x: number; y: number } | null>(null);

  useEffect(() => {
    const element = boxRef.current;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      // The chart sits in a page that scrolls, so a plain wheel belongs to the
      // page. Zooming asks for a modifier — which is also what a trackpad
      // pinch sends — the way an embedded map does it
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      setTransform((current) => zoomAt(current, Math.pow(0.998, event.deltaY), point));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const element = boxRef.current;
    if (!element) return;
    const distanceOf = (touches: TouchList) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );
    const centreOf = (touches: TouchList) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    });
    const onStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        pinch.current = { distance: distanceOf(event.touches), ...centreOf(event.touches) };
      }
    };
    // Two fingers move and scale the chart together; one finger is left to
    // the page, which has to stay scrollable on a phone
    const onMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !pinch.current) return;
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const distance = distanceOf(event.touches);
      const centre = centreOf(event.touches);
      const factor = distance / (pinch.current.distance || distance);
      const moved = { x: centre.x - pinch.current.x, y: centre.y - pinch.current.y };
      pinch.current = { distance, ...centre };
      const point = { x: centre.x - rect.left, y: centre.y - rect.top };
      setTransform((current) => {
        const scaled = zoomAt(current, factor, point);
        return { ...scaled, x: scaled.x + moved.x, y: scaled.y + moved.y };
      });
    };
    const onEnd = () => {
      pinch.current = null;
    };
    element.addEventListener('touchstart', onStart, { passive: true });
    element.addEventListener('touchmove', onMove, { passive: false });
    element.addEventListener('touchend', onEnd);
    return () => {
      element.removeEventListener('touchstart', onStart);
      element.removeEventListener('touchmove', onMove);
      element.removeEventListener('touchend', onEnd);
    };
  }, []);

  const onPointerDown = (event: React.PointerEvent) => {
    // A finger dragging is the page being scrolled, not the chart being moved
    if (event.pointerType === 'touch' || event.button !== 0 || pinch.current) return;
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      tx: transform.x,
      ty: transform.y,
      moved: false,
    };
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const held = drag.current;
    if (!held) return;
    const dx = event.clientX - held.x;
    const dy = event.clientY - held.y;
    if (!held.moved && Math.hypot(dx, dy) < 4) return;
    held.moved = true;
    setTransform((current) => ({ ...current, x: held.tx + dx, y: held.ty + dy }));
  };

  const endDrag = () => {
    // A click that followed a drag was the reader moving the chart, not choosing
    const moved = drag.current?.moved;
    drag.current = null;
    return moved;
  };

  const choose = (id: string) => {
    if (drag.current?.moved) return;
    if (id === ROOT_ID) return;
    onSelect(id);
    // The details open over the right of the chart, so a body chosen there
    // would disappear behind them: move it out from under
    const item = chart.byId.get(id);
    if (!item || size.width < 760) return;
    const edge = size.width - PANEL_W;
    const right = pointOf(item).x + (mode === 'tree' ? CARD_W / 2 : 90);
    if (right > edge) {
      const shift = right - edge + 24;
      setTransform((current) => ({ ...current, x: current.x - shift }));
    }
  };

  /* ---- folding branches away ---- */
  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setLevel = (value: string) => {
    setDepth(value);
    setCollapsed(value === 'all' ? new Set() : collapseBeyond(nodes, Number(value)));
  };

  /* ---- the one under the pointer ---- */
  const pointOf = (item: ChartItem): { x: number; y: number } => {
    if (mode === 'tree') {
      return {
        x: transform.x + (item.x + CARD_W / 2) * transform.k,
        y: transform.y + item.y * transform.k,
      };
    }
    const [x, y] = polar(item.radius, item.angle);
    return { x: transform.x + x * transform.k, y: transform.y + y * transform.k };
  };

  const dimmedFor = (item: ChartItem): boolean => {
    if (hits.size) return !hits.has(item.id) && !lineage.has(item.id);
    if (hoveredItem) return !lineage.has(item.id);
    return false;
  };

  /* ---- taking a copy away ---- */
  const download = async (format: 'png' | 'svg') => {
    const source = svgRef.current;
    if (!source) return;
    const pad = 40;
    const clone = source.cloneNode(true) as SVGSVGElement;
    const width = extent.maxX - extent.minX + pad * 2;
    const height = extent.maxY - extent.minY + pad * 2;
    clone.setAttribute('width', String(Math.round(width)));
    clone.setAttribute('height', String(Math.round(height)));
    clone.setAttribute(
      'viewBox',
      `${extent.minX - pad} ${extent.minY - pad} ${width} ${height}`,
    );
    clone.querySelector('[data-pan]')?.setAttribute('transform', '');
    const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    background.setAttribute('x', String(extent.minX - pad));
    background.setAttribute('y', String(extent.minY - pad));
    background.setAttribute('width', String(width));
    background.setAttribute('height', String(height));
    background.setAttribute('fill', '#ffffff');
    clone.insertBefore(background, clone.firstChild);
    const markup = new XMLSerializer().serializeToString(clone);
    const name = `${countryName.toLowerCase().replace(/\s+/g, '-')}-government`;

    if (format === 'svg') {
      save(new Blob([markup], { type: 'image/svg+xml' }), `${name}.svg`);
      return;
    }
    try {
      const image = new Image();
      image.src = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(markup)))}`;
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('The chart could not be drawn as an image'));
      });
      const canvas = document.createElement('canvas');
      const scale = Math.min(2, 4000 / Math.max(width, height));
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('No canvas');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => blob && save(blob, `${name}.png`), 'image/png');
    } catch {
      // Rather than nothing at all: the drawing itself is always good
      save(new Blob([markup], { type: 'image/svg+xml' }), `${name}.svg`);
    }
  };

  const legend = useMemo(() => legendOf(colourBy, whole, branches), [colourBy, whole, branches]);
  const wedges = useMemo(() => (mode === 'radial' ? branchWedges(chart) : []), [mode, chart]);

  return (
    <div
      className={`overflow-hidden bg-white ${
        expanded ? 'flex h-full flex-col' : 'rounded-2xl border border-neutral-200'
      } ${className}`}
    >
      {/* ---- what to show ---- */}
      {/* On a phone the search and full screen share the first line; from a
          tablet up full screen sits at the far end */}
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200/70 px-3 py-2.5">
        <SearchInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Find a body or a person"
          className="order-1 min-w-0 flex-1 sm:w-52 sm:flex-none"
        />
        {search.trim() && (
          <Pill tone={hits.size ? 'neutral' : 'outline'} className="order-1">
            {hits.size ? plural(hits.size, 'match', 'matches') : 'Nothing matches'}
          </Pill>
        )}
        {onToggleExpanded && (
          <FullscreenButton active={expanded} onToggle={onToggleExpanded} className="order-2 sm:order-3 sm:ml-auto" />
        )}
        <div className="order-3 -mx-3 flex max-w-[calc(100%+1.5rem)] items-center gap-2 overflow-x-auto px-3 sm:order-2 sm:mx-0 sm:max-w-full sm:px-0">
          <SegmentedControl
            label="Shape"
            className="flex-shrink-0"
            items={MODES}
            value={mode}
            onChange={(value) => {
              const next = value as ChartMode;
              setMode(next);
              // A ring of the top level alone says nothing: the ring is there
              // to show the whole state at once
              if (next === 'radial' && depth === '1') setLevel('all');
            }}
          />
          <SegmentedControl
            label="Levels"
            className="flex-shrink-0"
            items={DEPTHS}
            value={depth}
            onChange={setLevel}
          />
          <Menu
            align="left"
            trigger={(props) => (
              <Button size="xs" variant="secondary" {...props}>
                <Palette className="h-3.5 w-3.5" strokeWidth={1.75} />
                {COLOURS.find((option) => option.value === colourBy)?.label ?? 'Colour'}
              </Button>
            )}
            items={COLOURS.map((option) => ({
              label: option.label,
              icon: colourBy === option.value ? Check : undefined,
              onSelect: () => setColourBy(option.value),
            }))}
          />
          <Menu
            trigger={(props) => (
              <IconButton label="Take a copy of the chart" size="sm" {...props}>
                <Download className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
            )}
            items={[
              { label: 'Download PNG', icon: ImageIcon, onSelect: () => void download('png') },
              { label: 'Download SVG', icon: FileCode, onSelect: () => void download('svg') },
            ]}
          />
        </div>
      </div>

      {/* ---- the chart ---- */}
      <div
        ref={boxRef}
        className={`relative ${expanded ? 'min-h-0 flex-1' : 'h-[62vh] min-h-[380px]'} touch-pan-y bg-[radial-gradient(#e7e7e7_1px,transparent_1px)] [background-size:22px_22px]`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={() => {
          endDrag();
          setHovered(null);
        }}
      >
        <svg
          ref={svgRef}
          width={size.width || 800}
          height={size.height || 480}
          className="block select-none"
          // Named on the element itself so a downloaded copy keeps the face
          fontFamily="Geist, 'Plus Jakarta Sans', system-ui, sans-serif"
          role="img"
          aria-label={`The structure of the state of ${countryName}`}
        >
          <g
            data-pan
            transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}
            style={{
              transition: reducedMotion || drag.current ? undefined : 'transform 120ms ease-out',
            }}
          >
            {/* links first, so nothing is drawn over an entity */}
            <g fill="none">
              {chart.links.map((link) => {
                const onPath = lineage.has(link.target.id) && lineage.has(link.source.id);
                const colour = colourOf(link.target, colourBy, branches);
                return (
                  <path
                    key={link.id}
                    d={mode === 'tree' ? treeLink(link.source, link.target) : radialLink(link.source, link.target)}
                    stroke={onPath ? colour : '#d4d4d4'}
                    strokeWidth={onPath ? 1.8 : 1}
                    strokeOpacity={
                      hits.size || hoveredItem ? (onPath ? 0.95 : 0.25) : mode === 'tree' ? 0.8 : 0.55
                    }
                  />
                );
              })}
            </g>

            {mode === 'radial' && (
              <g>
                {/* what each branch of the state weighs, as a slice of the whole */}
                {wedges.map((wedge) => {
                  const colour = branches.get(wedge.id) ?? '#a3a3a3';
                  const lit = !lineage.size || lineage.has(wedge.id);
                  const [lx, ly] = polar(chart.radius + 96, wedge.middle);
                  const turned = (wedge.middle * 180) / Math.PI;
                  const left = Math.cos(wedge.middle) < 0;
                  return (
                    <g key={wedge.id} opacity={lit ? 1 : 0.35}>
                      <path
                        d={wedgePath(44, chart.radius + 74, wedge.from, wedge.to)}
                        fill={tint(colour, 0.05)}
                        stroke={tint(colour, 0.14)}
                        strokeWidth={0.75}
                      />
                      {(wedge.to - wedge.from) * (chart.radius + 96) * transform.k > 44 && (
                        <text
                          transform={`translate(${lx},${ly}) rotate(${left ? turned + 180 : turned})`}
                          textAnchor={left ? 'end' : 'start'}
                          dominantBaseline="middle"
                          fontSize={12}
                          fontWeight={600}
                          fill={colour}
                        >
                          {fitText(wedge.label, 190, 12)}
                          <tspan fill="#a3a3a3" fontWeight={400}>
                            {' '}
                            {wedge.size}
                          </tspan>
                        </text>
                      )}
                    </g>
                  );
                })}
                <circle r={30} fill="#0a0a0a" />
                <text y={4} textAnchor="middle" fontSize={10} fill="#ffffff" fontWeight={600}>
                  {fitText(countryName, 50, 10)}
                </text>
              </g>
            )}

            {chart.items.map((item) => {
              if (item.id === ROOT_ID) {
                return mode === 'tree' ? (
                  <g key={item.id} transform={`translate(${item.x},${item.y})`}>
                    <rect width={CARD_W} height={CARD_H} rx={13} fill="#0a0a0a" />
                    <text x={20} y={30} fontSize={13} fill="#ffffff">
                      {fitText(countryName, CARD_W - 40, 13)}
                    </text>
                    <text x={20} y={48} fontSize={11} fill="#a3a3a3">
                      {plural(insights.entities, 'body', 'bodies')} mapped
                    </text>
                  </g>
                ) : null;
              }
              const colour = colourOf(item, colourBy, branches);
              const common = {
                item,
                colour,
                selected: selectedId === item.id,
                dimmed: dimmedFor(item),
                onSelect: () => choose(item.id),
              };
              return (
                <g
                  key={item.id}
                  onMouseEnter={() => setHovered(item.id)}
                  onMouseLeave={() => setHovered((current) => (current === item.id ? null : current))}
                >
                  {mode === 'tree' ? (
                    <EntityCard {...common} onToggle={() => toggle(item.id)} />
                  ) : (
                    <RadialNode
                      {...common}
                      showLabel={
                        labelRoom(item, chart) * transform.k > 13 ||
                        hits.has(item.id) ||
                        selectedId === item.id
                      }
                    />
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* ---- what is under the pointer ---- */}
        {hoveredItem && hoveredItem.id !== ROOT_ID && (
          <HoverCard item={hoveredItem} at={pointOf(hoveredItem)} box={size} />
        )}

        {/* ---- moving about ---- */}
        {/* on the left, because the details of a chosen body open over the right */}
        <div
          role="toolbar"
          aria-label="Chart controls"
          className="absolute bottom-3 left-3 z-10 flex flex-col items-center gap-0.5 rounded-full bg-white p-1 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]"
        >
          <IconButton
            label="Zoom in"
            size="sm"
            onClick={() =>
              setTransform((current) =>
                zoomAt(current, 1.35, { x: size.width / 2, y: size.height / 2 }),
              )
            }
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <IconButton
            label="Zoom out"
            size="sm"
            onClick={() =>
              setTransform((current) =>
                zoomAt(current, 1 / 1.35, { x: size.width / 2, y: size.height / 2 }),
              )
            }
          >
            <Minus className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <IconButton label="Fit the whole state" size="sm" onClick={fit}>
            <Scan className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          {selectedItem && (
            <IconButton
              label="Centre on what is selected"
              size="sm"
              onClick={() => {
                const reach = mode === 'tree' ? CARD_W : 180;
                const centre =
                  mode === 'tree'
                    ? { x: selectedItem.x + CARD_W / 2, y: selectedItem.y + CARD_H / 2 }
                    : (() => {
                        const [x, y] = polar(selectedItem.radius, selectedItem.angle);
                        return { x, y };
                      })();
                setTransform(
                  frame(
                    {
                      minX: centre.x - reach,
                      minY: centre.y - reach / 2,
                      maxX: centre.x + reach,
                      maxY: centre.y + reach / 2,
                    },
                    size,
                    40,
                    1.4,
                  ),
                );
              }}
            >
              <Crosshair className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          )}
        </div>

        {/* ---- what the colours mean ---- */}
        <div className="pointer-events-none absolute left-3 top-3 z-10 hidden max-w-[60%] flex-wrap gap-x-3 gap-y-1 rounded-xl bg-white/85 px-2.5 py-2 text-[11px] text-neutral-500 shadow-sm ring-1 ring-black/[0.05] backdrop-blur-sm sm:flex">
          {legend.map((entry) => (
            <span key={entry.label} className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{ backgroundColor: entry.colour }}
              />
              <span className="truncate">{entry.label}</span>
            </span>
          ))}
        </div>
      </div>

      {/* ---- what the chart says ---- */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-neutral-200/70 px-3.5 py-2.5 text-[11.5px] text-neutral-500">
        <span className="text-ink">{plural(insights.entities, 'body', 'bodies')}</span>
        <span>{plural(insights.levels, 'level')} deep</span>
        <span className="inline-flex items-center gap-1.5">
          <UserRound className="h-3 w-3 text-emerald-600" strokeWidth={1.9} />
          {insights.staffed} heads recorded
        </span>
        {insights.researchOnly > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full border border-dashed border-neutral-400" />
            {insights.researchOnly} known only to research
          </span>
        )}
        {insights.empty > 0 && <span>{insights.empty} with nobody</span>}
        {insights.contested > 0 && (
          <span className="inline-flex items-center gap-1.5 text-amber-700">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            {insights.contested} with two people in one post
          </span>
        )}
        {insights.placedElsewhere > 0 && (
          <span className="inline-flex items-center gap-1.5 text-violet-700">
            <span className="h-2 w-2 rounded-full bg-violet-500" />
            {insights.placedElsewhere} filed away from the map
          </span>
        )}
        {insights.notInDirectory > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <Layers className="h-3 w-3" strokeWidth={1.75} />
            {insights.notInDirectory} not in the directory
          </span>
        )}
        {insights.pending > 0 && (
          <span className="inline-flex items-center gap-1.5 text-primary-700">
            <span className="h-2 w-2 rounded-full bg-primary-600" />
            {plural(insights.pending, 'change')} waiting
          </span>
        )}
        {loadingPeople && <span className="text-neutral-400">Looking up who holds the posts…</span>}
        <span className="ml-auto hidden text-neutral-400 sm:inline">
          Drag to move · ⌘ or Ctrl and scroll to zoom
        </span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Bits and pieces                                                     */
/* ------------------------------------------------------------------ */

const HoverCard: React.FC<{
  item: ChartItem;
  at: { x: number; y: number };
  box: { width: number; height: number };
}> = ({ item, at, box }) => {
  const width = 248;
  const left = Math.min(Math.max(12, at.x - width / 2), Math.max(12, box.width - width - 12));
  // Whichever side of the body has the room, and never taller than that room:
  // a card that runs off the top of the chart lands on the controls above it
  const below = box.height - at.y > at.y;
  return (
    <div
      className="pointer-events-none absolute z-20 w-[248px] overflow-hidden rounded-xl bg-white p-3 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)] ring-1 ring-black/[0.06]"
      style={{
        left,
        top: below ? at.y + 26 : undefined,
        bottom: below ? undefined : Math.max(12, box.height - at.y + 14),
        maxHeight: Math.max(120, below ? box.height - at.y - 38 : at.y - 26),
      }}
    >
      <p className="text-[12.5px] leading-snug text-ink">{item.label}</p>
      <p className="mt-0.5 text-[11px] text-neutral-400">
        {item.kind === 'country' ? 'The state' : KIND_LABEL[item.kind]}
        {item.node?.acronym ? ` · ${item.node.acronym}` : ''}
        {item.descendants > 0 ? ` · ${plural(item.descendants, 'body', 'bodies')} under it` : ''}
      </p>
      {item.holders.length > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {item.holders.slice(0, 3).map((person) => (
            <li key={person.stakeholderId} className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-sky-50 text-[9px] font-medium text-sky-700">
                {person.imageId ? (
                  <AuthImage
                    mediaId={person.imageId}
                    alt=""
                    className="h-full w-full object-cover"
                    fallback={<span>{initials(person.name)}</span>}
                  />
                ) : (
                  initials(person.name)
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] text-ink">{person.name}</span>
                {person.role && (
                  <span className="block truncate text-[10.5px] text-neutral-400">{person.role}</span>
                )}
              </span>
            </li>
          ))}
          {item.holders.length > 3 && (
            <li className="text-[11px] text-neutral-400">
              and {item.holders.length - 3} more recorded here
            </li>
          )}
        </ul>
      ) : item.person ? (
        <p className="mt-2 text-[11.5px] text-neutral-500">
          <span className="text-ink">{item.person.name}</span>
          {item.person.role ? ` · ${item.person.role}` : ''}
          <span className="mt-0.5 block text-[11px] text-amber-700">
            Research found them; the directory has no role here
          </span>
        </p>
      ) : (
        <p className="mt-2 text-[11.5px] text-neutral-400">Nobody recorded here</p>
      )}
      {item.node?.head?.since && (
        <p className="mt-1.5 text-[11px] text-neutral-400">
          In post since {govDate(item.node.head.since)}
          {item.node.head.confirmedOn ? ` · confirmed ${govDate(item.node.head.confirmedOn)}` : ''}
        </p>
      )}
      {item.mapPlacement && (
        <p className="mt-1.5 text-[11px] leading-snug text-amber-700">
          Filed here in your directory; the map puts it {item.mapPlacement}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {item.contested && <Pill tone="amber">Two recorded as {item.contested}</Pill>}
        {item.pending > 0 && <Pill tone="red">{plural(item.pending, 'change')} waiting</Pill>}
        {!item.inDirectory && <Pill tone="outline">Not in the directory</Pill>}
        {item.node?.stale && (
          <Pill tone="amber">
            <TriangleAlert className="h-3 w-3" strokeWidth={1.75} /> Not found again
          </Pill>
        )}
      </div>
    </div>
  );
};

function legendOf(
  colourBy: ColourBy,
  chart: Chart,
  branches: Map<string, string>,
): Array<{ label: string; colour: string }> {
  switch (colourBy) {
    case 'kind': {
      const kinds = Array.from(new Set(chart.items.filter((item) => item.node).map((item) => item.kind)));
      return kinds
        .slice(0, 8)
        .map((kind) => ({ label: KIND_LABEL[kind as keyof typeof KIND_LABEL] ?? 'Other', colour: KIND_COLOUR[kind] }));
    }
    case 'status':
      return [
        { label: 'In the directory', colour: STATUS_COLOUR.applied },
        { label: 'Confirmed', colour: STATUS_COLOUR.confirmed },
        { label: 'Waiting for review', colour: STATUS_COLOUR.proposed },
      ];
    case 'people':
      return [
        { label: 'nobody', colour: peopleColour(0) },
        { label: '1–2', colour: peopleColour(2) },
        { label: '3–5', colour: peopleColour(5) },
        { label: '6–10', colour: peopleColour(10) },
        { label: '11+', colour: peopleColour(20) },
      ];
    case 'branch':
    default:
      return chart.root.children.slice(0, 7).map((child, index) => ({
        label: child.label,
        colour: branches.get(child.id) ?? BRANCH_PALETTE[index % BRANCH_PALETTE.length],
      }));
  }
}

function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
